import React, { useState, useEffect, useReducer, useContext, useMemo } from "react";
import { toast } from "react-toastify";
import { useHistory } from "react-router-dom";

import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import TableSortLabel from "@material-ui/core/TableSortLabel";
import SearchIcon from "@material-ui/icons/Search";
import TextField from "@material-ui/core/TextField";
import InputAdornment from "@material-ui/core/InputAdornment";
import SwapHorizIcon from "@material-ui/icons/SwapHoriz";
import Chip from "@material-ui/core/Chip";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";
import FormControl from "@material-ui/core/FormControl";
import InputLabel from "@material-ui/core/InputLabel";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogContentText from "@material-ui/core/DialogContentText";
import DialogActions from "@material-ui/core/DialogActions";
import Box from "@material-ui/core/Box";
import Typography from "@material-ui/core/Typography";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import CancelIcon from "@material-ui/icons/Cancel";
import WarningIcon from "@material-ui/icons/Warning";
import BusinessIcon from "@material-ui/icons/Business";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";

import api from "../../services/api";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";

const reducer = (state, action) => {
  if (action.type === "LOAD_COMPANIES") {
    const companies = action.payload;
    const newCompanies = [];

    companies.forEach((company) => {
      const companyIndex = state.findIndex((u) => u.id === company.id);
      if (companyIndex !== -1) {
        state[companyIndex] = company;
      } else {
        newCompanies.push(company);
      }
    });

    return [...state, ...newCompanies];
  }

  if (action.type === "RESET") {
    return [];
  }
};

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(1),
    overflowY: "scroll",
    ...theme.scrollbarStyles,
  },
  currentCompany: {
    backgroundColor: theme.palette.type === "dark" 
      ? "rgba(255, 193, 7, 0.15)" 
      : "rgba(255, 193, 7, 0.1)",
    borderLeft: "4px solid #ffc107",
  },
  filterContainer: {
    display: "flex",
    gap: theme.spacing(2),
    alignItems: "center",
    flexWrap: "wrap",
  },
  statusFilter: {
    minWidth: 150,
  },
  statusChip: {
    fontWeight: 600,
    fontSize: "0.75rem",
  },
  activeChip: {
    backgroundColor: "#4caf50",
    color: "#fff",
  },
  inactiveChip: {
    backgroundColor: "#f44336",
    color: "#fff",
  },
  tableHeader: {
    backgroundColor: theme.palette.type === "dark" 
      ? theme.palette.background.default 
      : "#f5f5f5",
    "& th": {
      fontWeight: 700,
      color: theme.palette.text.primary,
    },
  },
  sortLabel: {
    "&:hover": {
      color: theme.palette.primary.main,
    },
    "&.MuiTableSortLabel-active": {
      color: theme.palette.primary.main,
    },
  },
  confirmDialog: {
    "& .MuiDialog-paper": {
      borderRadius: 12,
      padding: theme.spacing(1),
    },
  },
  dialogIcon: {
    fontSize: 60,
    color: "#ff9800",
    marginBottom: theme.spacing(1),
  },
  companyInfo: {
    backgroundColor: theme.palette.type === "dark" 
      ? theme.palette.background.default 
      : "#f5f5f5",
    borderRadius: 8,
    padding: theme.spacing(2),
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(1),
  },
  destinationBox: {
    backgroundColor: theme.palette.type === "dark" 
      ? "rgba(33, 150, 243, 0.15)" 
      : "#e3f2fd",
    borderRadius: 8,
    border: "1px solid #2196f3",
    padding: theme.spacing(2),
    marginTop: theme.spacing(2),
  },
  accessButton: {
    background: "linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)",
    color: "#fff",
    fontWeight: 600,
    "&:hover": {
      background: "linear-gradient(45deg, #1976D2 30%, #00BCD4 90%)",
    },
  },
  currentButton: {
    backgroundColor: "#ffc107",
    color: "#000",
    fontWeight: 600,
    "&:disabled": {
      backgroundColor: "#ffc107",
      color: "#000",
      opacity: 0.8,
    },
  },
}));

const SuperAdmin = () => {
  const classes = useStyles();
  const history = useHistory();
  const { user } = useContext(AuthContext);

  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [companies, dispatch] = useReducer(reducer, []);
  const [switchingCompany, setSwitchingCompany] = useState(false);
  
  // Novos estados
  const [statusFilter, setStatusFilter] = useState("all");
  const [orderBy, setOrderBy] = useState("id");
  const [order, setOrder] = useState("asc");
  const [confirmDialog, setConfirmDialog] = useState({ open: false, company: null });

  // Verificar permissão de acesso
  useEffect(() => {
    if (user && !user.super) {
      toast.error("Você não tem permissão para acessar esta página");
      history.push("/");
    }
  }, [user, history]);

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [searchParam, statusFilter]);

  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      const fetchCompanies = async () => {
        try {
          const { data } = await api.get("/companies", {
            params: { 
              searchParam, 
              pageNumber,
              status: statusFilter !== "all" ? statusFilter : undefined
            },
          });
          dispatch({ type: "LOAD_COMPANIES", payload: data.companies });
          setHasMore(data.hasMore);
          setLoading(false);
        } catch (err) {
          toastError(err);
        }
      };
      fetchCompanies();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [searchParam, pageNumber, statusFilter]);

  const handleSearch = (event) => {
    setSearchParam(event.target.value.toLowerCase());
  };

  const handleStatusFilterChange = (event) => {
    setStatusFilter(event.target.value);
  };

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleOpenConfirmDialog = (company) => {
    setConfirmDialog({ open: true, company });
  };

  const handleCloseConfirmDialog = () => {
    setConfirmDialog({ open: false, company: null });
  };

  const handleConfirmSwitch = async () => {
    const company = confirmDialog.company;
    if (!company) return;

    handleCloseConfirmDialog();
    setSwitchingCompany(true);
    
    try {
      const { data } = await api.post("/auth/switch-company", {
        companyId: company.id,
      });

      localStorage.setItem("token", JSON.stringify(data.token));
      
      toast.success(`Acessando empresa "${company.name}"... Recarregando!`);
      
      setTimeout(() => {
        window.location.href = "/";
      }, 800);
    } catch (err) {
      toastError(err);
      setSwitchingCompany(false);
    }
  };

  const loadMore = () => {
    setPageNumber((prevState) => prevState + 1);
  };

  const handleScroll = (e) => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) {
      loadMore();
    }
  };

  // Função para normalizar status (boolean no banco)
  const normalizeStatus = (status) => {
    if (status === true || status === "true" || status === 1) return "active";
    return "inactive";
  };

  // Função para renderizar o chip de status
  const renderStatusChip = (status) => {
    const normalized = normalizeStatus(status);
    
    if (normalized === "active") {
      return (
        <Chip
          icon={<CheckCircleIcon style={{ color: "#fff" }} />}
          label="Ativo"
          size="small"
          className={`${classes.statusChip} ${classes.activeChip}`}
        />
      );
    }
    
    return (
      <Chip
        icon={<CancelIcon style={{ color: "#fff" }} />}
        label="Inativo"
        size="small"
        className={`${classes.statusChip} ${classes.inactiveChip}`}
      />
    );
  };

  // Ordenar empresas (filtro é feito no backend)
  const sortedCompanies = useMemo(() => {
    let sorted = [...companies];

    // Ordenar
    sorted.sort((a, b) => {
      let aValue, bValue;

      switch (orderBy) {
        case "id":
          aValue = a.id || 0;
          bValue = b.id || 0;
          break;
        case "name":
          aValue = (a.name || "").toLowerCase();
          bValue = (b.name || "").toLowerCase();
          break;
        case "email":
          aValue = (a.email || "").toLowerCase();
          bValue = (b.email || "").toLowerCase();
          break;
        case "plan":
          aValue = (a.plan?.name || "").toLowerCase();
          bValue = (b.plan?.name || "").toLowerCase();
          break;
        case "status":
          aValue = normalizeStatus(a.status);
          bValue = normalizeStatus(b.status);
          break;
        default:
          aValue = a.id || 0;
          bValue = b.id || 0;
      }

      if (typeof aValue === "string") {
        return order === "asc" 
          ? aValue.localeCompare(bValue) 
          : bValue.localeCompare(aValue);
      }

      return order === "asc" ? aValue - bValue : bValue - aValue;
    });

    return sorted;
  }, [companies, orderBy, order]);

  const columns = [
    { id: "id", label: "ID", sortable: true },
    { id: "name", label: "Nome", sortable: true },
    { id: "email", label: "Email", sortable: true },
    { id: "plan", label: "Plano", sortable: true },
    { id: "status", label: "Status", sortable: true },
    { id: "actions", label: "Ações", sortable: false },
  ];

  return (
    <MainContainer>
      <MainHeader>
        <Title>Acessar Empresas (Super Admin)</Title>
        <Box className={classes.filterContainer}>
          <TextField
            placeholder="Pesquisar empresas"
            type="search"
            value={searchParam}
            onChange={handleSearch}
            size="small"
            style={{ minWidth: 250 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon style={{ color: "gray" }} />
                </InputAdornment>
              ),
            }}
          />
          <FormControl variant="outlined" size="small" className={classes.statusFilter}>
            <InputLabel>Status</InputLabel>
            <Select
              value={statusFilter}
              onChange={handleStatusFilterChange}
              label="Status"
            >
              <MenuItem value="all">Todos</MenuItem>
              <MenuItem value="active">
                <Box display="flex" alignItems="center" gap={1}>
                  <CheckCircleIcon style={{ color: "#4caf50", fontSize: 18 }} />
                  <span style={{ marginLeft: 8 }}>Ativos</span>
                </Box>
              </MenuItem>
              <MenuItem value="inactive">
                <Box display="flex" alignItems="center" gap={1}>
                  <CancelIcon style={{ color: "#f44336", fontSize: 18 }} />
                  <span style={{ marginLeft: 8 }}>Inativos</span>
                </Box>
              </MenuItem>
            </Select>
          </FormControl>
          <Chip 
            label={`${sortedCompanies.length} empresas`} 
            color="primary" 
            variant="outlined"
          />
        </Box>
      </MainHeader>
      <Paper
        className={classes.mainPaper}
        variant="outlined"
        onScroll={handleScroll}
      >
        <Table size="small">
          <TableHead className={classes.tableHeader}>
            <TableRow>
              {columns.map((column) => (
                <TableCell key={column.id} align="center">
                  {column.sortable ? (
                    <TableSortLabel
                      active={orderBy === column.id}
                      direction={orderBy === column.id ? order : "asc"}
                      onClick={() => handleRequestSort(column.id)}
                      className={classes.sortLabel}
                    >
                      {column.label}
                    </TableSortLabel>
                  ) : (
                    column.label
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            <>
              {sortedCompanies.map((company) => (
                <TableRow 
                  key={company.id}
                  className={user.companyId === company.id ? classes.currentCompany : ""}
                >
                  <TableCell align="center">
                    <strong>{company.id || "-"}</strong>
                  </TableCell>
                  <TableCell align="center">{company.name || "-"}</TableCell>
                  <TableCell align="center">{company.email || "-"}</TableCell>
                  <TableCell align="center">
                    <Chip 
                      label={company.plan?.name || "Sem plano"} 
                      size="small" 
                      variant="outlined"
                      color={company.plan?.name ? "primary" : "default"}
                    />
                  </TableCell>
                  <TableCell align="center">
                    {renderStatusChip(company.status)}
                  </TableCell>
                  <TableCell align="center">
                    {user.companyId === company.id ? (
                      <Button
                        size="small"
                        variant="contained"
                        className={classes.currentButton}
                        disabled
                        startIcon={<BusinessIcon />}
                      >
                        Empresa Atual
                      </Button>
                    ) : (
                      <Button
                        size="small"
                        variant="contained"
                        className={classes.accessButton}
                        startIcon={<SwapHorizIcon />}
                        onClick={() => handleOpenConfirmDialog(company)}
                        disabled={switchingCompany}
                      >
                        Acessar
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {loading && <TableRowSkeleton columns={6} />}
            </>
          </TableBody>
        </Table>
      </Paper>

      {/* Dialog de Confirmação */}
      <Dialog
        open={confirmDialog.open}
        onClose={handleCloseConfirmDialog}
        className={classes.confirmDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" flexDirection="column" alignItems="center">
            <WarningIcon className={classes.dialogIcon} />
            <Typography variant="h6">Confirmar Troca de Empresa</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <DialogContentText align="center">
            Você está prestes a acessar outra empresa. Isso irá:
          </DialogContentText>
          <Box className={classes.companyInfo}>
            <Typography variant="body2" gutterBottom>
              <strong>• Trocar sua empresa atual</strong> para a empresa selecionada
            </Typography>
            <Typography variant="body2" gutterBottom>
              <strong>• Atribuir todas as filas</strong> da nova empresa ao seu usuário
            </Typography>
            <Typography variant="body2">
              <strong>• Recarregar a página</strong> para aplicar as alterações
            </Typography>
          </Box>
          {confirmDialog.company && (
            <Box className={classes.destinationBox}>
              <Typography variant="subtitle2" color="primary" gutterBottom>
                <BusinessIcon style={{ verticalAlign: "middle", marginRight: 8 }} />
                Empresa de destino:
              </Typography>
              <Typography variant="body1">
                <strong>{confirmDialog.company.name}</strong>
              </Typography>
              <Typography variant="body2" color="textSecondary">
                ID: {confirmDialog.company.id} | {confirmDialog.company.email}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions style={{ padding: "16px 24px" }}>
          <Button 
            onClick={handleCloseConfirmDialog} 
            color="default"
            variant="outlined"
          >
            Cancelar
          </Button>
          <Button 
            onClick={handleConfirmSwitch} 
            color="primary" 
            variant="contained"
            className={classes.accessButton}
            startIcon={<SwapHorizIcon />}
          >
            Confirmar e Acessar
          </Button>
        </DialogActions>
      </Dialog>
    </MainContainer>
  );
};

export default SuperAdmin;
